import { useState, useEffect } from "react";
import DB_PATH from "../DB_PATH";
import firebase from "firebase";
import Customer from "../../types/Customer";
import { functions } from "../firebase";
import {
  FreeAccessResult,
  setCustomerFreeAccess as setCustomerFreeAccessRequest,
} from "./customerAccessApi";

export default (communityId: string) => {
  const [users, setUsers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("CommunityId", communityId);

        // 1. Get the community document from Firestore
        const communitySnapshot = await firebase
          .firestore()
          .collection(DB_PATH.COMMUNITY)
          .doc(communityId)
          .collection("Members")
          .get();

        if (communitySnapshot.empty) {
          throw new Error("No members found in the community");
        }

        // 2. Retrieve all member IDs from the community's 'Members' subcollection
        const memberIds: string[] = [];
        communitySnapshot.forEach((doc) => {
          // Assuming each document in the 'Members' collection has a 'userId' field
          const userId = doc.id; // or doc.data().userId if user ID is stored differently
          memberIds.push(userId);
        });

        console.log("MembersIds", memberIds);

        // 3. Fetch user info from Firebase Realtime Database for each member
        const userPromises = memberIds.map(async (userId) => {
          const userSnapshot = await firebase
            .database()
            .ref(`${DB_PATH.USER}/${userId}`)
            .once("value");

          const userData = userSnapshot.val();

          if (userData) {
            return {
              id: userId,
              ...userData,
            };
          }
          return null;
        });

        // 4. Wait for all user data to be fetched
        const fetchedUsers = await Promise.all(userPromises);

        // 5. Filter out any null values (users not found in RTDB)
        const filteredUsers = fetchedUsers.filter((user) => user !== null);

        console.log("FilteredUsers", filteredUsers);

        // Optionally, add customers here
        // addCustomers(filteredUsers);

        setUsers(filteredUsers);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("An unknown error occurred")
        );
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [communityId]);

  const removeCustomer = async (userId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Remove userId from the Firestore community document
      const communityRef = firebase
        .firestore()
        .collection(DB_PATH.COMMUNITY)
        .doc(communityId);

      const communityDoc = await communityRef.get();

      if (!communityDoc.exists) {
        throw new Error("Community not found");
      }

      const data = communityDoc.data();
      const membersIds: string[] = data?.membersIds || [];

      if (!membersIds.includes(userId)) {
        throw new Error("User not found in community");
      }

      // Update the membersIds array
      const updatedMembersIds = membersIds.filter((id) => id !== userId);

      await communityRef.update({
        membersIds: updatedMembersIds,
      });

      console.log("CustomerRemoved", updatedMembersIds);

      // Update the local state
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("An unknown error occurred")
      );
    } finally {
      setLoading(false);
    }
  };

  const updateCustomer = async (
    userId: string,
    updatedData: Partial<Customer>
  ) => {
    try {
      setLoading(true);
      setError(null);

      console.log("updateCustomer called", { userId, updatedData });

      // Remove undefined values and fields that shouldn't be updated
      const cleanedData = Object.entries(updatedData).reduce((acc, [key, value]) => {
        // Skip undefined, null values and the id field
        if (value !== undefined && value !== null && key !== 'id') {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      console.log("Cleaned data for update", cleanedData);

      // Update customer data in Firebase Realtime Database
      await firebase
        .database()
        .ref(`${DB_PATH.USER}/${userId}`)
        .update(cleanedData);

      console.log("CustomerUpdated successfully in Firebase", { userId, cleanedData });

      // Update the local state with the new customer data
      setUsers((prevUsers) => {
        const updated = prevUsers.map((user) =>
          user.id === userId ? { ...user, ...cleanedData } : user
        );
        console.log("Local state updated", { prevUsers, updated });
        return updated;
      });
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(
        err instanceof Error ? err : new Error("An unknown error occurred")
      );
    } finally {
      setLoading(false);
    }
  };

  const createCustomer = async (customerData: Partial<Customer> & { password?: string }) => {
    try {
      setLoading(true);
      setError(null);

      const { password, ...customerDataWithoutPassword } = customerData;

      if (!customerData.email) {
        throw new Error("Email is required to create a customer");
      }

      if (!password) {
        throw new Error("Password is required to create a customer");
      }

      // Call the Cloud Function to create the customer
      // This uses Firebase Admin SDK on the server, so it won't log out the current user
      const createCustomerFunction = functions.httpsCallable("createCustomer");

      const result = await createCustomerFunction({
        email: customerData.email,
        password: password,
        fullName: customerDataWithoutPassword.fullName,
        phoneNumber: customerDataWithoutPassword.phoneNumber,
        communityId: communityId || '',
        isActive: customerDataWithoutPassword.isActive !== undefined ? customerDataWithoutPassword.isActive : true,
        experience: customerDataWithoutPassword.experience || 0,
      });

      const newUserId = result.data.userId;

      if (!newUserId) {
        throw new Error("Failed to create customer");
      }

      // Create the customer object to add to local state
      const newCustomer: Customer = {
        ...customerDataWithoutPassword,
        id: newUserId,
        fullName: customerDataWithoutPassword.fullName || "",
        communityId: communityId || '',
        experience: customerDataWithoutPassword.experience || 0,
        createdAt: Date.now(),
      };

      console.log("CustomerCreated", { newUserId, newCustomer });

      // Update the local state with the new customer
      setUsers((prevUsers) => [...prevUsers, newCustomer]);

      return newUserId;
    } catch (err: any) {
      console.error("Error creating customer:", err);

      // Handle Firebase Functions errors
      let errorMessage = "An unknown error occurred";

      if (err.code === "functions/already-exists") {
        errorMessage = "A user with this email already exists.";
      } else if (err.code === "functions/invalid-argument") {
        errorMessage = err.message || "Invalid input provided.";
      } else if (err.code === "functions/unauthenticated") {
        errorMessage = "You must be logged in to create customers.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      const error = new Error(errorMessage);
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const setCustomerFreeAccess = async (
    userId: string,
    durationDays: number | null,
    enabled = true
  ): Promise<FreeAccessResult> => {
    const result = await setCustomerFreeAccessRequest({
      communityId,
      userId,
      enabled,
      durationDays,
    });
    setUsers((previous) =>
      previous.map((customer) =>
        customer.id === userId
          ? {
              ...customer,
              manualFreeAccess: {
                active: result.active,
                durationDays,
                expiresAt: result.expiresAt,
              },
              ...(result.active ? { isSubscribed: true } : {}),
            }
          : customer
      )
    );
    return result;
  };

  return {
    users,
    loading,
    error,
    removeCustomer,
    updateCustomer,
    createCustomer,
    setCustomerFreeAccess,
  };
};
